import get from 'lodash/get';
import includes from 'lodash/includes';
import isNil from 'lodash/isNil';

import {
  getCroppedImageUrl,
  getUncroppedImageUrl
} from 'zoomforth_codesbane_viewer/viewer/sections/grid/tile_generators.js';

import {
  SECTION_PARALLAX_CLASSNAME,
  SECTION_GLASS_OVERLAY_CLASSNAME,
  SECTION_GLASS_OVERLAY_DARK_CLASSNAME,
  SECTION_CUSTOM_OVERLAY_CLASSNAME,
} from 'zoomforth_codesbane_viewer/const/css_wiki_classnames.js';


const BACKGROUND_TYPES_WITH_FALLBACK = ['video'];

const getBackgroundVideoParamBasicsFromContent = (contentItem, asset)=> {
  let posterImage = null;
  if (contentItem.tile_display_image) {
    posterImage = contentItem.tile_display_image;
  } else if (asset){
    posterImage = asset.displayImage;
  }

  if (isNil(asset) || isNil(asset['streams'])) {
    return {};
  }

  return {
    mpeg4Source: asset['streams']['mpeg4'],
    mpeg4LowQualitySource: asset['streams']['lowQuality'],
    subtitleTrack: asset['subtitles'],
    posterImage: posterImage
  };
};


/**
 * Section Background Notes:
 *
 * Section backgrounds are defined as an object of shape:
 *
 * {
 *   backgroundType,
 *   [backgroundColorProps],
 *   [backgroundImageProps],
 *   [backgroundVideoProps]
 * }
 *
 * Based on the type of background, various props are defined to help interpret
 * the background. This data is stored on the section itself. For some
 * background types (particularly videos), we can set a fallbackBackgroundData
 * object which is the same structure as the original backgroundData object.
 *
 * Both backgroundData and fallbackBackgroundData should be able to be parsed
 * by the following functions and rendered by the same set of components.
 *
 * Fallback data is used for mobile displays currently.
 */


/**
 * Background images can be cropped so we set an aspect ratio which mimics a 5
 * column grid which is sufficiently large enough based on some testing.
 */
export const getDefaultSectionBackgroundAssetRatio = ()=> (
  { sizex: 5, sizey: 4 }
);


/**
 * Get the video params needed to embed a video background into a section.
 */
export const getBackgroundVideoParams = (backgroundData) => {
  const {
    asset,
    backgroundType,
    contentItem,
    backgroundVideoLoop,
    backgroundVideoObjectFit,
    backgroundVideoObjectPosition,
    shouldAutoplayBackgroundVideo,
  } = backgroundData;

  if (backgroundType !== 'video' || !contentItem || !asset) {
    return {};
  }

  return {
    backgroundVideoObjectFit,
    backgroundVideoObjectPosition,
    shouldAutoplayBackgroundVideo,
    shouldBackgroundVideoLoop: backgroundVideoLoop,
    ...getBackgroundVideoParamBasicsFromContent(contentItem, asset)
  };
};


/**
 * Get the correct background image URL for an image background. This can be a
 * cropped image URL, uncompressed image URL, or an uncropped compressed image
 * URL.
 */
export const getBackgroundImageUrl = (backgroundData) => {
  const {
    asset,
    backgroundType,
    contentItem,
    isUsingUncompressedBackgroundImage
  } = backgroundData;

  if (backgroundType !== 'image' || !contentItem || !asset) {
    return undefined;
  }

  const { sizex, sizey } = getDefaultSectionBackgroundAssetRatio();
  const croppedImageUrl = getCroppedImageUrl(contentItem, asset, sizex, sizey);

  // whether the data is uncompressed or not, we should always attempt to get a crop first.
  if (croppedImageUrl) {
    return croppedImageUrl;
  }

  if (isUsingUncompressedBackgroundImage) {
    return asset.originalImage;
  }

  return getUncroppedImageUrl(contentItem, asset);
};

/**
 * Get the background classes needed to define a section with a background
 * color or background image.
 */
export const getBackgroundClasses = (backgroundData)=> {
  const {
    isParallaxEnabled,
    isGlassOverlayEnabled,
    isCustomOverlayEnabled,
    glassOverlayType,
    backgroundType,
  } = backgroundData;

  if (!backgroundType || backgroundType === 'none') {
    return {};
  }

  if (backgroundType === 'image') {
    return {
      [SECTION_PARALLAX_CLASSNAME]: isParallaxEnabled,
      [SECTION_GLASS_OVERLAY_CLASSNAME]: isGlassOverlayEnabled,
      [SECTION_GLASS_OVERLAY_DARK_CLASSNAME]: isGlassOverlayEnabled && glassOverlayType === 'dark',
      [SECTION_CUSTOM_OVERLAY_CLASSNAME]: isCustomOverlayEnabled,
    };
  }

  if (backgroundType === 'video') {
    return {
      [SECTION_GLASS_OVERLAY_CLASSNAME]: isGlassOverlayEnabled,
      [SECTION_GLASS_OVERLAY_DARK_CLASSNAME]: isGlassOverlayEnabled && glassOverlayType === 'dark',
      [SECTION_CUSTOM_OVERLAY_CLASSNAME]: isCustomOverlayEnabled,
    };
  }

  return {};
};


/**
 * Get the background style props needed to defined a section with a background
 * color or background image.
 */
export const getBackgroundStyle = (backgroundData)=> {
  const {
    backgroundType,
    backgroundColor,
    backgroundImagePosition,
    backgroundImageRepeat,
    backgroundImageSize,
    backgroundZfImage
  } = backgroundData;

  if (!backgroundType || backgroundType === 'none') {
    return {};
  }

  if (backgroundType === 'color') {
    return { backgroundColor };
  }

  if (backgroundType === 'image') {
    const legacyBackgroundImageUrl = getBackgroundImageUrl(backgroundData);
    const backgroundImageUrl = backgroundZfImage && backgroundZfImage.src;

    if (!(backgroundImageUrl || legacyBackgroundImageUrl)) {
      return {};
    }

    return {
      backgroundImage: `url(${backgroundImageUrl || legacyBackgroundImageUrl})`,
      backgroundPosition: backgroundImagePosition,
      backgroundRepeat: backgroundImageRepeat,
      backgroundSize: backgroundImageSize
    };
  }

  return {};
};


/**
 * Given a backgroundData object, find the relevant props needed to handle the
 * UI for the background chooser.
 */
export const getBackgroundDataProps = (backgroundData) => {
  const { asset, backgroundType } = backgroundData;

  const canUseUncompressedImage = (
    backgroundType === 'image' &&
    !!asset &&
    !asset.isVector &&
    !!asset.originalImage
  );

  const displayFilename = get(asset, 'display_filename');
  const isParallaxEnabled = backgroundType === 'image' ? get(backgroundData, 'isParallaxEnabled', false) : false;

  return {
    canUseUncompressedImage,
    isParallaxEnabled,
    displayFilename,
    ...backgroundData
  };
};

export const getCanUseFallbackBackground = (primaryBackgroundData) =>
  includes(BACKGROUND_TYPES_WITH_FALLBACK, primaryBackgroundData.backgroundType);

export const getBorderStyle = (borderData) => {
  const {
    borderType,
    borderWidth,
    borderColor,
    isBorderRadiusEnabled,
    borderRadiusTop,
    borderRadiusRight,
    borderRadiusBottom,
    borderRadiusLeft
  } = borderData;

  if (!borderType) {
    return {};
  }

  const styles = {};

  styles.borderWidth = `${borderWidth || 0}px`;

  styles.borderStyle = borderType;

  if (borderColor) {
    styles.borderColor = borderColor;
  }

  if (isBorderRadiusEnabled) {
    const top = borderRadiusTop || 0;
    const right = borderRadiusRight || 0;
    const bottom = borderRadiusBottom || 0;
    const left = borderRadiusLeft || 0;
    
    if (top === right && right === bottom && bottom === left) {
      styles.borderRadius = `${top}px`;
    } else {
      styles.borderRadius = `${top}px ${right}px ${bottom}px ${left}px`;
    }
  }

  return styles;
};
